import {
    FGStack,
    FGTile,
    FGTypography,
    FGIcons,
    FGStackItem,
} from '@fg/design-system';
import { useOutsideClick } from 'hooks/useOutsideClick';
import React, {
    ReactNode,
    useState,
    useCallback,
    useImperativeHandle,
} from 'react';
import cx from 'classnames';

type FGStackAlignItems =
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'stretch'
    | 'baseline';

type PickerTogglerProps = {
    children: ReactNode;
    label?: string;
    labelComponent?: ReactNode;
    icon: string;
    expandedIcon?: string;
    collapsedIcon?: string;
    alignItems?: FGStackAlignItems;
    // TODO: could move into labelComponent, but that needs a layout change here, verify across all PickerToggler usages first
    trailingActions?: ReactNode;
};

const PickerToggler = React.forwardRef((props: PickerTogglerProps, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);
    const onClickOutside = useCallback(e => {
        setIsOpen(false);
    }, []);
    const clickRef = useOutsideClick(onClickOutside);

    useImperativeHandle(
        ref,
        () => {
            return {
                close() {
                    setIsOpen(false);
                },
            };
        },
        []
    );

    // this margin helps push down the left and right icons surrounding the items list based on Figma design
    const marginTop = props.alignItems === 'flex-start' ? 12 : 0;

    return (
        <div
            ref={clickRef}
            style={{ cursor: 'pointer', width: '100%' }}
            className="picker-toggler">
            <FGStack onClick={toggleOpen}>
                <FGTile
                    padding="small"
                    appearance="secondary"
                    className={cx('button', 'picker-toggler-inner-tile', {
                        active: isOpen,
                    })}>
                    {/*todo: find alternative to width: 100% */}
                    <FGStack
                        justifyContent="space-between"
                        alignItems={props.alignItems || 'center'}
                        style={{ width: '100%' }}>
                        <FGStackItem>
                            <FGStack
                                gap={8}
                                alignItems={props.alignItems || 'center'}>
                                <FGStack
                                    alignItems={props.alignItems || 'center'}>
                                    {/* todo: fix type (make Icons public)*/}
                                    {/*@ts-ignore*/}
                                    <FGIcons icon={props.icon}
                                        style={{ marginTop }}
                                    />
                                </FGStack>
                                {props.label && (
                                    <FGTypography
                                        tag="label"
                                        variant="label2"
                                        color="neutral">
                                        {props.label}
                                    </FGTypography>
                                )}
                                {props.labelComponent && (
                                    <>{props.labelComponent}</>
                                )}
                            </FGStack>
                        </FGStackItem>
                        <FGStackItem>
                            <FGStack
                                gap={4}
                                alignItems="center"
                                style={{ marginTop }}>
                                {props.trailingActions && (
                                    <FGStackItem>
                                        {props.trailingActions}
                                    </FGStackItem>
                                )}
                                <FGStackItem
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}>
                                    <FGIcons
                                        style={{ pointerEvents: 'none' }}
                                        //@ts-ignore
                                        icon={
                                            isOpen
                                                ? props.expandedIcon ||
                                                  'Expanded'
                                                : props.collapsedIcon ||
                                                  'Collapsed'
                                        }
                                    />
                                </FGStackItem>
                            </FGStack>
                        </FGStackItem>
                    </FGStack>
                </FGTile>
            </FGStack>
            {isOpen && <div>{props.children}</div>}
        </div>
    );
});

// todo: move somewhere else
export const PICKER_GAP = 4;

export default PickerToggler;
